import { useContext, useEffect, useState } from "react";
import { observer } from "mobx-react";
import { Link } from "react-router-dom";
import { MdForward10, MdReplay10 } from "react-icons/md";
import { BsFillPlayFill, BsPauseFill } from "react-icons/bs";
import { IoMdClose } from "react-icons/io";
import { ActionIcon, Affix, Anchor, Button, Group, Paper, Stack, Text } from "@mantine/core";
import styles from "./styles/Player.module.css";
import { RangeSlider } from "./RangeSlider";
import { AudioContext, AudioState } from "@/context/audio";
import { trackEvent } from "@/core/analytics";
import { useStores } from "@/hooks/useStores";
import { usePlayEpisode } from "@/hooks/usePlayEpisode";
import { Urls } from "@/core/urls";
import { createTimeStructure, filterTitle, getPodcastImage, ImageSizes, timeAgo } from "@/core/helpers";

const PlayerToggle = observer(({ episode, podcast }: { episode: Episode; podcast: Podcast }) => {
    const { play, pause, isPlaying } = usePlayEpisode(episode, podcast);

    return (
        <button
            aria-label={isPlaying ? "Pausa avsnitt" : "Spela avsnitt"}
            className={styles.toggle}
            onClick={isPlaying ? pause : play}
            type="button">
            {isPlaying ? <BsPauseFill size={26} /> : <BsFillPlayFill size={26} />}
        </button>
    );
});

export const Player = observer(() => {
    const { playerStore } = useStores();
    const sound = useContext<AudioState>(AudioContext);

    const ratios = [0.5, 0.75, 1, 1.25, 1.5, 2];
    const [time, setTime] = useState(0);
    const currentRate = sound.rate();
    const ratio = Math.max(0, ratios.indexOf(currentRate));

    useEffect(() => {
        const interval = setInterval(() => {
            if (sound.audio) {
                setTime(sound.seek());
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [sound]);

    const changeTime = (newValue: number | number[]) => {
        const value = Array.isArray(newValue) ? newValue[0] : newValue;
        setTime(value);
        sound.seek(value);
        trackEvent({
            category: "Player",
            action: "SliderClick",
            label: `${playerStore.currentPodcast.slug}-${playerStore.currentEpisode.slug}`,
            value,
        });
    };

    const soundLoaded = () => sound.audio !== undefined;
    const getCurrentTime = () => (soundLoaded() ? sound.seek() : null);

    const close = (event?: React.MouseEvent<HTMLElement>) => {
        event?.preventDefault();
        playerStore.close();
        sound.unload();
    };

    const jump = (jumpAmount: number) => {
        const current = getCurrentTime();
        if (current !== null) {
            const next = Math.max(0, Math.floor(current) + jumpAmount);
            setTime(next);
            sound.seek(next);
        }
    };

    const rewind = (event?: React.MouseEvent<HTMLElement>) => {
        event?.preventDefault();
        jump(-10);
        trackEvent({
            category: "Player",
            action: "Rewind",
            label: `${playerStore.currentPodcast.slug}-${playerStore.currentEpisode.slug}`,
        });
    };

    const forward = (event?: React.MouseEvent<HTMLElement>) => {
        event?.preventDefault();
        jump(10);
        trackEvent({
            category: "Player",
            action: "Forward",
            label: `${playerStore.currentPodcast.slug}-${playerStore.currentEpisode.slug}`,
        });
    };

    const showRatio = (event?: React.MouseEvent<HTMLElement>) => {
        event?.preventDefault();
        const maxLength = ratios.length - 1;
        const next = ratio === maxLength ? 0 : ratio + 1;
        sound.rate(ratios[next]);
        trackEvent({
            category: "Player",
            action: "Ratio",
            label: `${playerStore.currentPodcast.slug}-${playerStore.currentEpisode.slug}`,
        });
    };

    if (!playerStore.active) {
        return null;
    }

    return (
        <Affix className={styles.affix} position={{ bottom: 16, left: 0, right: 0 }}>
            <Paper className={styles.player} px="md" py="md" radius="md" shadow="xl" withBorder>
                <ActionIcon
                    aria-label="Stäng spelaren"
                    className={styles.close}
                    onClick={close}
                    radius="md"
                    size="lg"
                    variant="subtle">
                    <IoMdClose size={18} />
                </ActionIcon>

                <Group align="flex-start" className={styles.header} gap="md" wrap="nowrap">
                    <Anchor component={Link} to={Urls.podcastHome(playerStore.currentPodcast.slug)} underline="never">
                        <img
                            alt="podcast img"
                            className={styles.podcastimg}
                            loading="lazy"
                            src={getPodcastImage(playerStore.currentPodcast, ImageSizes.Small)}
                        />
                    </Anchor>

                    <Stack className={styles.meta} gap={4}>
                        <Anchor
                            component={Link}
                            className={styles.title}
                            to={`/podd/${playerStore.currentPodcast.slug}/${playerStore.currentEpisode.slug}`}
                            underline="never">
                            <span>
                                {filterTitle(playerStore.currentEpisode.title, playerStore.currentPodcast.name)}
                            </span>
                        </Anchor>
                        <Group gap="xs" wrap="wrap">
                            <Anchor
                                component={Link}
                                fw={500}
                                size="sm"
                                to={`/podd/${playerStore.currentPodcast.slug}`}
                                underline="hover">
                                {playerStore.currentPodcast.name}
                            </Anchor>
                            <Text c="dimmed" size="sm">
                                {timeAgo(playerStore.currentEpisode.published)}
                            </Text>
                        </Group>
                    </Stack>
                </Group>

                <div className={styles.timeline}>
                    <RangeSlider value={time} onChange={changeTime} min={0} max={sound.duration()} />
                </div>

                <Group className={styles.controls} justify="space-between" wrap="wrap">
                    <Group gap="xs" wrap="nowrap">
                        <ActionIcon
                            aria-label="Spola tillbaka tio sekunder"
                            onClick={rewind}
                            radius="md"
                            size="xl"
                            variant="default">
                            <MdReplay10 size={20} />
                        </ActionIcon>
                        <PlayerToggle
                            episode={playerStore.currentEpisode}
                            key={playerStore.currentEpisode.guid}
                            podcast={playerStore.currentPodcast}
                        />
                        <ActionIcon
                            aria-label="Spola fram tio sekunder"
                            onClick={forward}
                            radius="md"
                            size="xl"
                            variant="default">
                            <MdForward10 size={20} />
                        </ActionIcon>
                    </Group>

                    <Text c="dimmed" size="sm">
                        {createTimeStructure(time).formatted} / {createTimeStructure(sound.duration()).formatted}
                    </Text>

                    <Button
                        aria-label="Ändra uppspelningshastighet"
                        onClick={showRatio}
                        radius="md"
                        size="compact-md"
                        variant="default">
                        {Math.round(ratios[ratio] * 100)}%
                    </Button>
                </Group>
            </Paper>
        </Affix>
    );
});
